import type { MouseEvent } from "react";

import type { Translate } from "../../i18n";
import { track } from "../../lib/analytics";

/**
 * Small splash chrome pieces that App.tsx renders around the lazily loaded
 * splash (the public nav lives in App.tsx). Kept in their own tiny module so
 * importing them never pulls the splash chunk into the entry bundle.
 */

type AuthMode = "login" | "signup";
type Screen = "splash" | "auth" | "dashboard" | "api" | "docs";

/** SPA routes the splash calls to action open; also their no-JS hrefs. */
export const SPLASH_CTA_HREFS = {
  providerSignup: "/?screen=auth&mode=signup",
  apiAccess: "/?screen=api",
  dashboard: "/?screen=dashboard"
} as const;

/** Id of the splash content the skip link jumps to. */
export const SPLASH_CONTENT_ID = "splash-content";

/**
 * A same-tab primary click on a link the SPA handles itself. Modified clicks
 * (new tab or window, download) and other buttons keep the browser default,
 * so the real href still works; without JavaScript the href is the route.
 */
export function handleSpaLink(event: MouseEvent<HTMLAnchorElement>, navigate: () => void): void {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }
  event.preventDefault();
  navigate();
}

/**
 * The nav call to action for anonymous visitors ("Become a provider"), next
 * to Login in the public nav (founder decision 2026-10-30, audit S-10).
 * Signed-in visitors already get the nav Dashboard button.
 */
export function SplashNavCta({
  isAuthenticated,
  setAuthMode,
  setScreen,
  t
}: {
  isAuthenticated: boolean;
  setAuthMode: (mode: AuthMode) => void;
  setScreen: (screen: Screen) => void;
  t: Translate;
}) {
  if (isAuthenticated) return null;
  return (
    <a
      className="wsf-nav-cta"
      data-testid="splash-nav-cta"
      href={SPLASH_CTA_HREFS.providerSignup}
      onClick={(event) =>
        handleSpaLink(event, () => {
          track.ctaClicked("provider_signup_nav");
          setAuthMode("signup");
          setScreen("auth");
        })
      }
    >
      {t("splash.providerSignup")}
    </a>
  );
}

/**
 * "Skip to content" for keyboard users (audit S-11): the first focusable
 * element on the splash, visible only while focused. It moves focus past the
 * nav without adding a history entry (the href is the no-JS fallback).
 */
export function SplashSkipLink({ t }: { t: Translate }) {
  return (
    <a
      className="wsf-skip-link"
      href={`#${SPLASH_CONTENT_ID}`}
      onClick={(event) => {
        // Never a hash navigation while the app runs: it would add a history
        // entry, and before the lazy splash renders there is no target yet.
        event.preventDefault();
        const target = document.getElementById(SPLASH_CONTENT_ID);
        if (!target) return;
        target.focus({ preventScroll: true });
        target.scrollIntoView({ block: "start" });
      }}
    >
      {t("splash.skipToContent")}
    </a>
  );
}
